import * as THREE from "three";

const UP = new THREE.Vector3(0, 1, 0);

export class AxisIndicatorGizmo extends THREE.Group {

    static #shaftRadius = 0.035;
    static #positiveShaftLength = 0.62;
    static #positiveConeRadius = 0.09;
    static #positiveConeHeight = 0.22;
    static #positiveLabelDistance = 1;

    static #negativeShaftLength = 0.34;
    static #negativeConeRadius = 0.07;
    static #negativeConeHeight = 0.14;
    static #negativeLabelDistance = 0.62;
    static #negativeOpacity = 0.45;

    static #labelSize = 0.46;

    static #axes = [
        { axis: "x", dir: new THREE.Vector3(1, 0, 0), color: 0xdc4a4a },
        { axis: "y", dir: new THREE.Vector3(0, 1, 0), color: 0x4dbb4d },
        { axis: "z", dir: new THREE.Vector3(0, 0, 1), color: 0x4a7fdc }
    ];

    constructor() {
        super();
        this.name = "axisIndicatorGizmo";

        for (const { axis, dir, color } of AxisIndicatorGizmo.#axes) {
            this.#buildArrow(axis, dir, color, false);
            this.#buildArrow(axis, dir.clone().negate(), color, true);
        }
    }

    #buildArrow(axis, dir, color, negative) {
        const shaftLength = negative ? AxisIndicatorGizmo.#negativeShaftLength : AxisIndicatorGizmo.#positiveShaftLength;
        const shaftRadius = negative ? AxisIndicatorGizmo.#shaftRadius * 0.8 : AxisIndicatorGizmo.#shaftRadius;
        const coneRadius = negative ? AxisIndicatorGizmo.#negativeConeRadius : AxisIndicatorGizmo.#positiveConeRadius;
        const coneHeight = negative ? AxisIndicatorGizmo.#negativeConeHeight : AxisIndicatorGizmo.#positiveConeHeight;
        const labelDistance = negative ? AxisIndicatorGizmo.#negativeLabelDistance : AxisIndicatorGizmo.#positiveLabelDistance;
        const opacity = negative ? AxisIndicatorGizmo.#negativeOpacity : 0.95;

        const quaternion = new THREE.Quaternion().setFromUnitVectors(UP, dir);
        const material = new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity });

        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(shaftRadius, shaftRadius, shaftLength, 8), material);
        shaft.position.copy(dir).multiplyScalar(shaftLength / 2);
        shaft.quaternion.copy(quaternion);
        shaft.renderOrder = 0;
        this.add(shaft);

        const cone = new THREE.Mesh(new THREE.ConeGeometry(coneRadius, coneHeight, 12), material);
        cone.position.copy(dir).multiplyScalar(shaftLength + coneHeight / 2);
        cone.quaternion.copy(quaternion);
        cone.renderOrder = 1;
        this.add(cone);

        const label = AxisIndicatorGizmo.#createLabel(negative ? `-${axis.toUpperCase()}` : axis.toUpperCase(), color, negative);
        label.position.copy(dir).multiplyScalar(labelDistance);
        label.userData.axis = axis;
        label.userData.role = negative ? "negative" : "positive";
        this.add(label);
    }

    static #createLabel(text, color, dim) {
        const size = 96;
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext("2d");

        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
        ctx.fillStyle = `#${color.toString(16).padStart(6, "0")}`;
        ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.font = `bold ${text.length > 1 ? 56 : 74}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, size / 2, size / 2 + 2);

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true, opacity: dim ? AxisIndicatorGizmo.#negativeOpacity + 0.25 : 1 });
        const sprite = new THREE.Sprite(material);
        sprite.renderOrder = 2;
        sprite.scale.setScalar(AxisIndicatorGizmo.#labelSize * (dim ? 0.82 : 1));
        return sprite;
    }

    dispose() {
        this.children.forEach(child => {
            child.geometry?.dispose();
            child.material?.map?.dispose();
            child.material?.dispose();
        });
    }
}
